import { IAlarm } from "../../Models/IAlarm";
import { ISentinelRich } from "../../Models/ISentinel";
import { ISentinelTrace } from "../../Models/ISentinelTrace";
import { getSensorIconTooltip } from "../../Utils/SensorMaps";
import { ISentinelTraceContainer } from "./StatisticPage";

const getDateFallingGroup = (date: string, dateList: string[]) => {
  let test = new Date(date);
  let lastMatch = null;
  for (let i = 0; i < dateList.length; i++) {
    let begin = dateList[i].split(" - ")[0];
    if (test >= new Date(begin)) {
      lastMatch = dateList[i];
    }
  }
  return lastMatch as string;
};

const dateRange = (startDate: string, endDate: string, groupBy: string) => {
  var start = startDate.split("-");
  var end = endDate.split("-");
  var startYear = parseInt(start[0]);
  var endYear = parseInt(end[0]);
  var dates: string[] = [];

  for (var i = startYear; i <= endYear; i++) {
    var endMonth = i != endYear ? 11 : parseInt(end[1]) - 1;
    var startMon = i === startYear ? parseInt(start[1]) - 1 : 0;
    for (var j = startMon; j <= endMonth; j = j > 12 ? j % 12 || 11 : j + 1) {
      var month = j + 1;
      var displayMonth = month < 10 ? "0" + month : month;
      dates.push([i, displayMonth, "01"].join("-"));
    }
  }

  // month grouping
  if (groupBy === "0") {
    return dates;
  }

  // trimester grouping
  if (groupBy === "1") {
    return dates
      .map((x, i) => {
        if (i % 3 === 0) {
          let right = dates[i + 2];
          return x + " - " + (right ? right : "Oggi");
        } else {
          return null;
        }
      })
      .filter((x: string | null) => x) as string[];
  }

  // semester grouping
  if (groupBy === "2") {
    return dates
      .map((x, i) => {
        if (i % 6 === 0) {
          let right = dates[i + 5];
          return x + " - " + (right ? right : "Oggi");
        } else {
          return null;
        }
      })
      .filter((x: string | null) => x) as string[];
  }

  return [];
};

const usageToBarChartData = (
  usageData: ISentinelTraceContainer[],
  patientId: number,
  minDate: string,
  maxDate: string,
  aggregateBy: string
) => {
  let reducedUsageData: ISentinelTraceContainer[] = usageData.filter(
    (x: ISentinelTraceContainer) =>
      x.sentinel.patient.id === patientId || patientId === -1
  );

  let datasets: any = {};
  let labels: string[] = dateRange(minDate, maxDate, aggregateBy);

  for (let i = 0; i < reducedUsageData.length; i++) {
    let r = reducedUsageData[i];
    let s = r.sentinel.identifier;

    if (!datasets.hasOwnProperty(s)) {
      datasets[s] = {
        label: s,
        data: {},
      };

      for (let j = 0; j < labels.length; j++) {
        datasets[s].data[labels[j]] = 0;
      }
    }
    let validityDay = r.item.activeOnDay.split("T")[0];
    let dateFallsInGroup = getDateFallingGroup(validityDay, labels);
    datasets[s].data[dateFallsInGroup] += 1;
  }

  let readyDatasets = Object.values(datasets);

  return {
    labels: labels,
    datasets: readyDatasets.map((x: any) => {
      x.data = Object.values(x.data);
      return x;
    }),
  };
};
const usageToPieChartData = (
  usageData: ISentinelTraceContainer[],
  patientId: number
) => {
  let reducedUsageData = usageData.filter(
    (x) => x.sentinel.patient.id === patientId || patientId === -1
  );
  let values: any = {};
  for (let i = 0; i < reducedUsageData.length; i++) {
    let r = reducedUsageData[i];
    if (!values.hasOwnProperty(r.sentinel.identifier)) {
      values[r.sentinel.identifier] = 0;
    }
    values[r.sentinel.identifier] += 1;
  }

  return {
    labels: Object.keys(values),
    datasets: [
      {
        data: Object.values(values),
      },
    ],
  };
};

const alarmsToPieChartData = (alarms_: IAlarm[], patientId: number) => {
  let alarms: IAlarm[] = alarms_.filter(
    (x: IAlarm) => x.patient.id === patientId || patientId === -1
  );
  let labels: any = {};
  let datas: any = {};
  for (let i = 0; i < alarms.length; i++) {
    let alarmType = alarms[i].sensorType;
    if (alarmType.startsWith("lidar")) {
      alarmType = "lidarAggregated";
    }
    let tip = getSensorIconTooltip(alarmType);

    if (!datas.hasOwnProperty(alarmType)) {
      datas[alarmType] = 0;
    }

    labels[alarmType] = tip;
    datas[alarmType] += 1;
  }

  return {
    labels: Object.values(labels),
    datasets: [
      {
        data: Object.values(datas),
      },
    ],
  };
};

const alarmsToBarChartData = (
  alarms_: IAlarm[],
  patientId: number,
  minDate: string,
  maxDate: string,
  aggregateBy: string
) => {
  let alarms: IAlarm[] = alarms_.filter(
    (x: IAlarm) => x.patient.id === patientId || patientId === -1
  );

  let datasets: any = {};
  let labels: string[] = dateRange(minDate, maxDate, aggregateBy);
  for (let i = 0; i < alarms.length; i++) {
    let alarmType = alarms[i].sensorType;
    if (alarmType.startsWith("lidar")) {
      alarmType = "lidarAggregated";
    }

    if (!datasets.hasOwnProperty(alarmType)) {
      datasets[alarmType] = {
        label: getSensorIconTooltip(alarmType),
        data: {},
      };

      for (let j = 0; j < labels.length; j++) {
        datasets[alarmType].data[labels[j]] = 0;
      }
    }
    let creationDate = alarms[i].createdAt.split("T")[0];
    let dateFallsInGroup: string = getDateFallingGroup(creationDate, labels);
    datasets[alarmType].data[dateFallsInGroup] += 1;
  }

  let readyDatasets = Object.values(datasets);

  let final = {
    labels: labels,
    datasets: readyDatasets.map((x: any) => {
      x.data = Object.values(x.data);
      return x;
    }),
  };

  return final;
};

const Manipulators = {
  usageToBarChartData,
  usageToPieChartData,
  alarmsToPieChartData,
  alarmsToBarChartData,
};

export default Manipulators;
